import styled from "@emotion/styled";
import { PrimaryButtonSmall } from "../../buttons/PrimaryButton";
import StripeLogoBox from "../../icons/StripeLogoBox";
import {
  ParagraphHeaderSmall,
  ParagraphSmall,
} from "../../typography/Paragraph";
import { COLOR_TERTIARY, MOBILE_SCREEN } from "../../theme";
import Loading from "../../Loading";
import ErrorMessage from "../../Error";
import { formatErrorString } from "../../../utils/format";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";

const BannerContainer = styled.div<{
  color?: string;
}>`
  width: 100%;
  height: 128px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  background: ${COLOR_TERTIARY};
  border-radius: 8px;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.1);
  padding: 0px 24px;
  margin-top: 24px;
  ${MOBILE_SCREEN} {
    flex-direction: column;
    height: auto;
  }
`;

const BannerTextContainer = styled.div`
  flex: 1;
  margin-left: 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const BannerTitle = styled(ParagraphHeaderSmall)`
  font-weight: 600;
  line-height: 24px;
`;

const BannerSubtitle = styled(ParagraphSmall)`
  min-width: 80px;
  width: 100%;
`;

const ConnectButton = styled(PrimaryButtonSmall)`
  height: 48px;
  border-radius: 100px;
  padding: 12px 24px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 108px;
  max-width: 140px;
  white-space: nowrap;
`;

const AttorneyStripeConnectBanner = ({
  isLoading = false,
  error = undefined,
  onConnect,
  unresolvedRequirements = [],
  isLawFirmLead = false,
  payoutsEnabled = false,
}: {
  isLoading?: boolean;
  error?: FetchBaseQueryError | SerializedError | undefined;
  onConnect?: () => void;
  unresolvedRequirements?: string[];
  isLawFirmLead?: boolean;
  payoutsEnabled?: boolean;
}) => {
  const getBannerState = () => {
    // Not a law firm lead - waiting for colleague
    if (!isLawFirmLead) {
      return {
        title: "Waiting on your colleague to finish Stripe setup",
        subtitle:
          "Once your colleague finishes, you'll be able to receive payments.",
        buttonText: "Connect",
        showButton: false,
      };
    }

    // Payouts enabled with specific ID verification requirement
    if (
      payoutsEnabled &&
      unresolvedRequirements.length === 1 &&
      unresolvedRequirements[0] === "individual.verification.document"
    ) {
      return {
        title: "Finish Verification Now",
        subtitle:
          "Please provide the requested identity verification information to avoid having your payouts paused.",
        buttonText: "Verify Now",
        showButton: true,
      };
    }

    // Payouts enabled but has other unresolved requirements
    if (payoutsEnabled && unresolvedRequirements.length > 0) {
      return {
        title: "Finish your Stripe setup",
        subtitle:
          "You are approved by Stripe, but still have some pending requirements. Please provide the requested information to avoid having your payouts paused.",
        buttonText: "Complete Setup",
        showButton: true,
      };
    }

    // Payouts enabled and no unresolved requirements
    if (payoutsEnabled) {
      return {
        title: "Manage Payments",
        subtitle: "Sign into Stripe Connect",
        buttonText: "Go to Stripe",
        showButton: true,
      };
    }

    // No unresolved requirements but payouts not enabled
    if (unresolvedRequirements.length === 0) {
      return {
        title: "Your Stripe account is connected and in review",
        subtitle: "Almost done setup! Stripe is reviewing your account.",
        buttonText: "Connect",
        showButton: false,
      };
    }

    // Default case - initial setup
    return {
      title: "Setup your Stripe Connect account",
      subtitle:
        "Set up your Stripe connect account to receive automatic payments",
      buttonText: "Connect",
      showButton: true,
    };
  };

  if (isLoading) {
    return (
      <BannerContainer>
        <StripeLogoBox />
        <BannerTextContainer>
          <Loading />
        </BannerTextContainer>
      </BannerContainer>
    );
  }

  if (error) {
    return (
      <BannerContainer>
        <StripeLogoBox />
        <BannerTextContainer>
          <ErrorMessage>{formatErrorString(error)}</ErrorMessage>
        </BannerTextContainer>
      </BannerContainer>
    );
  }

  const {
    title: bannerTitle,
    subtitle: bannerSubtitle,
    buttonText,
    showButton,
  } = getBannerState();

  return (
    <BannerContainer>
      <StripeLogoBox />
      <BannerTextContainer>
        <BannerTitle>{bannerTitle}</BannerTitle>
        <BannerSubtitle>{bannerSubtitle}</BannerSubtitle>
      </BannerTextContainer>
      {showButton && (
        <ConnectButton onClick={onConnect}>{buttonText}</ConnectButton>
      )}
    </BannerContainer>
  );
};

export default AttorneyStripeConnectBanner;
